"use client"

import { useEffect, useState } from "react"
import Link from "next/link"

import type { AuthMode } from "@/components/auth/auth-mode"
import { SignupFlowLines } from "@/components/auth/signup-flow-lines"
import { SignupMarketingPanel } from "@/components/auth/signup-marketing-panel"
import { SignupSecurityBadges } from "@/components/auth/signup-security-badges"
import { useAuthForm } from "@/components/auth/use-auth-form"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { ErrorIcon, PaymentIcon, SecurityIcon } from "@/icons"
import { GoogleLogo } from "@/icons/google-logo"
import { MicrosoftLogo } from "@/icons/microsoft-logo"
import { RhythmsLogo } from "@/icons/rhythms-logo"
import { SlackLogo } from "@/icons/slack-logo"
import { cn } from "@/lib/utils"

interface UnifiedAuthFormProps {
  googleOAuthUrl: string
  microsoftOAuthUrl: string
  onEmailSubmit: (
    formData: FormData
  ) => Promise<{ requiresMagicAuth?: boolean; error?: string | null }>
  onOAuthSubmit: (oauthUrl: string, provider: string) => Promise<void>
  isSubmitting: boolean
  /** Blocks submit without the in-flight copy, e.g. while Teams detection is pending. */
  isSubmitDisabled?: boolean
  error: string | null
  loginHint: string | null
  initialAuthMode: AuthMode
  slackOAuthUrl: string
}

function AuthErrorBanner({ error }: { error: string | null | undefined }) {
  // Retain the previous error text while the banner is collapsing so the
  // message doesn't disappear before the height/opacity transition finishes.
  const [lastError, setLastError] = useState<string | null>(error ?? null)
  const isOpen = Boolean(error)

  useEffect(() => {
    if (error) setLastError(error)
  }, [error])

  return (
    <div
      role={isOpen ? "alert" : undefined}
      aria-hidden={!isOpen}
      className={cn(
        "grid w-full overflow-hidden transition-[grid-template-rows,opacity,margin] duration-300 ease-out motion-reduce:transition-none",
        isOpen
          ? "mt-4 mb-4 grid-rows-[1fr] opacity-100"
          : "mt-0 mb-0 grid-rows-[0fr] opacity-0"
      )}
    >
      <div className="min-h-0">
        <div className="border-destructive/20 bg-destructive/10 w-full rounded-lg border p-3">
          <div className="flex items-center gap-2">
            <ErrorIcon
              className="text-destructive size-5 shrink-0"
              aria-hidden="true"
            />
            <p className="text-destructive text-sm">{lastError ?? ""}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

function AuthToggleLink({
  toggleHref,
  prompt,
  linkText,
}: {
  toggleHref: string
  prompt: string
  linkText: string
}) {
  return (
    <p className="text-content-secondary mt-6 text-center text-sm">
      {prompt}{" "}
      <Button
        variant="link"
        render={<Link href={toggleHref}>{linkText}</Link>}
      />
    </p>
  )
}

function AuthTermsFooter() {
  return (
    <div className="text-content-tertiary mt-4 text-center text-xs">
      By continuing, you agree to our{" "}
      <Button
        variant="link-muted"
        size="sm"
        render={
          <a
            href="https://www.rhythms.ai/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
          >
            terms of service
          </a>
        }
      />{" "}
      and{" "}
      <Button
        variant="link-muted"
        size="sm"
        render={
          <a
            href="https://www.rhythms.ai/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            privacy policy
          </a>
        }
      />
    </div>
  )
}

const SIGNUP_TRUST_ITEMS = [
  { Icon: PaymentIcon, label: "No credit card required" },
  { Icon: SecurityIcon, label: "SOC2 Type II" },
] as const

function SignupTrustLine() {
  return (
    <div className="text-content-tertiary mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
      {SIGNUP_TRUST_ITEMS.map(({ Icon, label }) => (
        <span key={label} className="flex items-center gap-1 text-xs">
          <Icon className="size-3.5 shrink-0" aria-hidden="true" />
          {label}
        </span>
      ))}
    </div>
  )
}

export function UnifiedAuthForm({
  googleOAuthUrl,
  microsoftOAuthUrl,
  onEmailSubmit,
  onOAuthSubmit,
  isSubmitting,
  isSubmitDisabled = false,
  error,
  loginHint,
  initialAuthMode,
  slackOAuthUrl,
}: UnifiedAuthFormProps) {
  const {
    isSignUp,
    toggleHref,
    formAction,
    displayError,
    dismissError,
    handleGoogleOAuth,
    handleMicrosoftOAuth,
    handleSlackOAuth,
  } = useAuthForm({
    googleOAuthUrl,
    microsoftOAuthUrl,
    slackOAuthUrl,
    onEmailSubmit,
    onOAuthSubmit,
    error,
    initialAuthMode,
  })

  if (isSignUp) {
    return (
      <SignUpLayout
        formAction={formAction}
        isSubmitting={isSubmitting}
        isSubmitDisabled={isSubmitDisabled}
        loginHint={loginHint}
        displayError={displayError}
        dismissError={dismissError}
        googleOAuthUrl={googleOAuthUrl}
        microsoftOAuthUrl={microsoftOAuthUrl}
        handleGoogleOAuth={handleGoogleOAuth}
        handleMicrosoftOAuth={handleMicrosoftOAuth}
        slackOAuthUrl={slackOAuthUrl}
        handleSlackOAuth={handleSlackOAuth}
        toggleHref={toggleHref}
      />
    )
  }

  return (
    <SignInLayout
      formAction={formAction}
      isSubmitting={isSubmitting}
      isSubmitDisabled={isSubmitDisabled}
      loginHint={loginHint}
      displayError={displayError}
      dismissError={dismissError}
      googleOAuthUrl={googleOAuthUrl}
      microsoftOAuthUrl={microsoftOAuthUrl}
      slackOAuthUrl={slackOAuthUrl}
      handleGoogleOAuth={handleGoogleOAuth}
      handleMicrosoftOAuth={handleMicrosoftOAuth}
      handleSlackOAuth={handleSlackOAuth}
      toggleHref={toggleHref}
    />
  )
}

interface AuthLayoutProps {
  formAction: (payload: FormData) => void
  isSubmitting: boolean
  isSubmitDisabled: boolean
  loginHint: string | null
  displayError: string | null | undefined
  dismissError: () => void
  googleOAuthUrl: string
  microsoftOAuthUrl: string
  slackOAuthUrl: string
  handleGoogleOAuth: (e: React.MouseEvent) => void
  handleMicrosoftOAuth: (e: React.MouseEvent) => void
  handleSlackOAuth: (e: React.MouseEvent) => void
  toggleHref: string
}

function SignUpLayout({
  formAction,
  isSubmitting,
  isSubmitDisabled,
  loginHint,
  displayError,
  dismissError,
  googleOAuthUrl,
  microsoftOAuthUrl,
  handleGoogleOAuth,
  handleMicrosoftOAuth,
  slackOAuthUrl,
  handleSlackOAuth,
  toggleHref,
}: AuthLayoutProps) {
  const [emailValue, setEmailValue] = useState(loginHint ?? "")

  useEffect(() => {
    if (loginHint) {
      setEmailValue(loginHint)
    }
  }, [loginHint])

  return (
    <div className="flex min-h-screen w-full">
      <SignupMarketingPanel className="hidden lg:flex lg:w-1/2" />
      <div className="relative flex w-full flex-col items-center px-6 py-10 lg:w-1/2">
        {/* Same converging flow-field as the left panel, kept very subtle and
            faded toward the edges, so both sides share one texture. Sits at
            z-0 with the content lifted to z-10 so it never bleeds through the
            card. */}
        <SignupFlowLines
          className="z-0 opacity-[0.06]"
          maskImage="radial-gradient(85% 72% at 50% 42%, #000 0%, rgba(0,0,0,0) 85%)"
        />
        {/* Top zone: equal flex-1 with the bottom zone so the card sits at the
            column's exact vertical center, lining up with the testimonial card
            on the left at any viewport height. Holds the mobile-only logo
            (shown only below lg, where the left marketing panel and its brand
            mark are hidden) just above the card. */}
        <div className="relative z-10 flex w-full flex-1 flex-col items-center justify-end">
          <RhythmsLogo
            width={48}
            height={48}
            className="text-brand mb-6 lg:hidden"
            aria-label="Rhythms logo"
          />
        </div>

        {/* Sign-up card — vertically centered in the column. */}
        <div className="relative z-10 flex w-full max-w-md shrink-0 flex-col items-center">
          <div className="bg-surface-primary border-border-strong w-full rounded-2xl border p-8">
            <div className="mb-6 flex flex-col items-center">
              <h1 className="text-content-primary mb-1 text-center text-xl font-bold">
                Try Rhythms for free
              </h1>
              <SignupTrustLine />
            </div>

            <div className="flex w-full flex-col gap-3">
              <Button
                type="button"
                variant="outline"
                size="xl"
                onClick={handleGoogleOAuth}
                disabled={!googleOAuthUrl}
                className="w-full"
                aria-label="Continue with Google"
              >
                <GoogleLogo className="size-5" aria-hidden="true" />
                Continue with Google
              </Button>

              <Button
                type="button"
                variant="outline"
                size="xl"
                onClick={handleMicrosoftOAuth}
                disabled={!microsoftOAuthUrl}
                className="w-full"
                aria-label="Continue with Microsoft"
              >
                <MicrosoftLogo className="size-5" aria-hidden="true" />
                Continue with Microsoft
              </Button>

              <Button
                type="button"
                variant="outline"
                size="xl"
                onClick={handleSlackOAuth}
                disabled={!slackOAuthUrl}
                className="w-full"
                aria-label="Continue with Slack"
              >
                <SlackLogo className="size-5" aria-hidden="true" />
                Continue with Slack
              </Button>
            </div>

            <div className="my-6 flex w-full items-center">
              <Separator className="flex-1" />
              <span className="text-content-tertiary mx-4 text-xs font-normal tracking-wide whitespace-nowrap uppercase">
                OR
              </span>
              <Separator className="flex-1" />
            </div>

            <form
              className="flex w-full flex-col gap-1"
              action={formAction}
              aria-label="Email sign up form"
            >
              <label
                htmlFor="signup-email"
                className="text-content-primary mb-1 text-sm font-normal"
              >
                Work email
              </label>
              <Input
                id="signup-email"
                name="email"
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                required
                aria-label="Work email address"
                variant="auth"
                disabled={isSubmitting}
                value={emailValue}
                onChange={(e) => {
                  setEmailValue(e.target.value)
                  dismissError()
                }}
              />
              <Button
                type="submit"
                variant="default"
                size="xl"
                className="mt-2 w-full"
                aria-label="Sign up for free"
                disabled={isSubmitting || isSubmitDisabled}
              >
                {isSubmitting ? "Loading..." : "Sign up for free"}
              </Button>
            </form>

            <AuthErrorBanner error={displayError} />

            <AuthToggleLink
              toggleHref={toggleHref}
              prompt="Already have an account?"
              linkText="Sign in"
            />
          </div>
        </div>

        {/* Bottom zone mirrors the top zone (equal flex-1) so the card stays at
            the column's vertical center. Terms sit just under the card and the
            security badges pin to the bottom; justify-between guarantees a gap
            so the two never collide on short desktop screens. */}
        <div className="relative z-10 flex w-full flex-1 flex-col items-center justify-between">
          <AuthTermsFooter />
          <SignupSecurityBadges className="w-full" />
        </div>
      </div>
    </div>
  )
}

function SignInLayout({
  formAction,
  isSubmitting,
  isSubmitDisabled,
  loginHint,
  displayError,
  dismissError,
  googleOAuthUrl,
  microsoftOAuthUrl,
  slackOAuthUrl,
  handleGoogleOAuth,
  handleMicrosoftOAuth,
  handleSlackOAuth,
  toggleHref,
}: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex w-full max-w-md flex-col items-center">
        <RhythmsLogo
          width={48}
          height={48}
          className="text-brand mb-8"
          aria-label="Rhythms logo"
        />

        <h1 className="text-content-primary mb-8 text-center text-xl font-bold">
          Sign in to Rhythms
        </h1>

        <div className="border-border bg-surface-primary w-full rounded-2xl border p-8 shadow-lg">
          <div className="flex w-full flex-col gap-3">
            <Button
              type="button"
              variant="outline"
              size="xl"
              onClick={handleGoogleOAuth}
              disabled={!googleOAuthUrl}
              className="w-full"
              aria-label="Continue with Google"
            >
              <GoogleLogo className="size-5" aria-hidden="true" />
              Continue with Google
            </Button>

            <Button
              type="button"
              variant="outline"
              size="xl"
              onClick={handleMicrosoftOAuth}
              disabled={!microsoftOAuthUrl}
              className="w-full"
              aria-label="Continue with Microsoft"
            >
              <MicrosoftLogo className="size-5" aria-hidden="true" />
              Continue with Microsoft
            </Button>

            <Button
              type="button"
              variant="outline"
              size="xl"
              onClick={handleSlackOAuth}
              disabled={!slackOAuthUrl}
              className="w-full"
              aria-label="Continue with Slack"
            >
              <SlackLogo className="size-5" aria-hidden="true" />
              Continue with Slack
            </Button>
          </div>

          <div className="my-6 flex w-full items-center">
            <Separator className="flex-1" />
            <span className="text-content-tertiary mx-4 text-sm font-normal">
              OR
            </span>
            <Separator className="flex-1" />
          </div>

          <form
            className="flex w-full flex-col gap-1"
            action={formAction}
            aria-label="Email sign in form"
          >
            <label
              htmlFor="email"
              className="text-content-primary mb-1 text-sm font-normal"
            >
              Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Your email address"
              autoComplete="email"
              required
              aria-label="Your email address"
              variant="auth"
              disabled={isSubmitting}
              key={loginHint ?? "email-input"}
              defaultValue={loginHint ?? ""}
              onChange={() => {
                dismissError()
              }}
            />
            <Button
              type="submit"
              variant="default"
              size="xl"
              className="mt-2 w-full"
              aria-label="Continue with email"
              disabled={isSubmitting || isSubmitDisabled}
            >
              {isSubmitting ? "Loading..." : "Continue"}
            </Button>
          </form>

          <AuthErrorBanner error={displayError} />
        </div>

        <AuthToggleLink
          toggleHref={toggleHref}
          prompt="New to Rhythms?"
          linkText="Sign up"
        />
        <AuthTermsFooter />
      </div>
    </div>
  )
}
